import { useCallback, useEffect, useRef } from "react";

import type { StateChange } from "@/hooks/useAppForegroundState";
import useAppForegroundState from "@/hooks/useAppForegroundState";

interface UseForegroundPushNotificationHandlerProps {
  isPushEnabled: boolean;
  registerForPushNotifications: () => Promise<boolean>;
}

/**
 * Custom hook to handle foreground state changes for push notifications
 *
 * This hook automatically re-registers for push notifications when the app
 * comes to the foreground, ensuring that the device push token is current
 * and registered with Sendbird. It includes a concurrency guard to prevent
 * multiple simultaneous registration attempts.
 *
 * @param isPushEnabled - Whether push notifications are enabled
 * @param registerForPushNotifications - Function to register for push notifications
 */
const useForegroundPushNotificationHandler = ({
  isPushEnabled,
  registerForPushNotifications,
}: UseForegroundPushNotificationHandlerProps) => {
  // Prevent concurrent calls to registerForPushNotifications to avoid infinite loops
  const isRegisteringPushNotifications = useRef(false);

  const handleForegroundStateChange = useCallback(
    async ({ nextState }: StateChange) => {
      if (!isPushEnabled || nextState !== "active") return;
      if (isRegisteringPushNotifications.current) return;

      // re-register for push notifications if the user has granted permissions
      // this retrieves the device push token and registers it with Sendbird
      isRegisteringPushNotifications.current = true;
      try {
        await registerForPushNotifications();
      } catch {
        // Silently handle registration errors - we'll try again on next foreground
      } finally {
        isRegisteringPushNotifications.current = false;
      }
    },
    [isPushEnabled, registerForPushNotifications],
  );

  const handleForegroundStateChangeRef = useRef(handleForegroundStateChange);

  useEffect(() => {
    handleForegroundStateChangeRef.current = handleForegroundStateChange;
  }, [handleForegroundStateChange]);

  useAppForegroundState({
    onChangeRef: handleForegroundStateChangeRef,
  });
};

export default useForegroundPushNotificationHandler;
